// src/prototype/pages/EAUpgradePage.tsx
// ============================================================
// EA UPGRADE / CHECKOUT PAGE
// 
// Stripe-like payment page for demo purposes.
// - Shows plan details and pricing ($149/mo, 12-month commitment)
// - Dummy credit card form (doesn't validate)
// - "Upgrade Now" button works without entering anything
// - Toggles to Pro experience on click
//
// PRICING MODEL:
// - $149 per month
// - 12-month subscription commitment
// - Monthly billing
// - Total contract value: $1,788
//
// VISUAL HIERARCHY (Jan 2025 Polish):
// - Pro card is visually dominant (stronger shadow, gradient)
// - Current Plan card is muted/secondary
// - Payment form is quieter (demo-friendly)
// - Trust badges directly under CTA
// - Professional "DVA Pro Unlocked" toast on upgrade
//
// POST-UPGRADE NAVIGATION:
// - Redirects to /opportunities (Evaluate & Shortlist) so users can
//   select finalists with their new Pro features
// ============================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEMO_CLIENT } from '../config/demoClient';
import { 
  ArrowLeft, 
  Check, 
  CreditCard,
  Lock,
  Shield,
  Building2,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { useDemoPlan } from '../../hooks/useDemoPlan';

// Mock personalization data

// Freemium features (condensed)
const freemiumFeatures = [
  'Top 3 prioritized use cases',
  'High-level ROI estimates',
  'Organization profile and baseline inputs',
  'Limited evaluation and no approval artifacts',
];

// Pro features - organized by category
const proFeatureCategories = [
  {
    title: 'Full Platform Access',
    features: [
      'Unlimited use cases (75+)',
      'Advanced ROI modeling with Conservative / Base / Aggressive scenarios',
      'Assumption validation and sensitivity analysis',
      'Custom category and measure weighting aligned to your organization\'s priorities',
    ],
  },
  {
    title: 'CFO-Ready Outputs',
    features: [
      'Executive one-pagers',
      'CFO-ready ROI summaries (PDF + Excel)',
      'Board-ready rationale and supporting materials',
      'Assumptions, risks, and defensibility documentation',
    ],
  },
  {
    title: 'Decision Transparency',
    features: [
      'Scoring rationalization views',
      'Priority comparison and shortlisting tools',
      'Deeper findings from surveys and strategic documents',
    ],
  },
];

// ============================================================
// SUCCESS TOAST COMPONENT
// ============================================================
interface SuccessToastProps {
  isVisible: boolean;
}

function SuccessToast({ isVisible }: SuccessToastProps) {
  if (!isVisible) return null;
  
  return (
    <div className="fixed top-6 right-6 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-lg px-5 py-4 flex items-start gap-3 min-w-[280px]">
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#18365E]">DVA Pro Unlocked</p>
          <p className="text-xs text-[#717182] mt-0.5">Select your finalists to continue…</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function EAUpgradePage() {
  const navigate = useNavigate();
  const { isPro, upgrade } = useDemoPlan();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // Form state (dummy - not validated)
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');

  // If already Pro, redirect to opportunities page (Evaluate & Shortlist)
  React.useEffect(() => {
    if (isPro && !showSuccessToast) {
      navigate('/opportunities', { replace: true });
    }
  }, [isPro, navigate, showSuccessToast]);

  const handleUpgrade = async () => {
    setIsProcessing(true);
    
    // Simulate brief processing delay (1000ms)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Upgrade to Pro
    upgrade();
    
    setIsProcessing(false);
    
    // Show success toast
    setShowSuccessToast(true);
    
    // Navigate to Evaluate & Shortlist after toast displays (~800ms)
    // User needs to select finalists before they can proceed to Activate
    setTimeout(() => {
      navigate('/opportunities');
    }, 800);
  };

  // Don't render checkout form for Pro users (they're being redirected)
  if (isPro && !showSuccessToast) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Success Toast */}
      <SuccessToast isVisible={showSuccessToast} />
      
      {/* Journey Bar */}
      <EAJourneyBarFrame sticky />

      {/* Main Content */}
      <main className="max-w-[1100px] mx-auto px-6 py-6">
        {/* Header - tightened spacing */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#18365E] mb-1.5">
            Unlock DVA Pro
          </h1>
          <p className="text-[#717182] max-w-lg mx-auto text-sm">
            Move from analysis to execution with CFO-grade ROI modeling and executive-ready materials.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column: Plan Comparison */}
          <div className="lg:col-span-2 space-y-4 order-2 lg:order-1">
            {/* Pro Plan - DOMINANT */}
            <div className="bg-gradient-to-br from-[#18365E] to-[#1e4175] rounded-xl p-5 text-white shadow-xl ring-1 ring-[#18365E]/20">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold tracking-tight">DVA Pro</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-[#FF6E4C] rounded uppercase tracking-wide">
                  Recommended
                </span>
              </div>
              
              {/* Pricing - tighter */}
              <div className="mb-0.5">
                <span className="text-3xl font-bold">$149</span>
                <span className="text-white/70 text-sm"> /month</span>
              </div>
              <p className="text-xs text-white/60 mb-4">
                12-month subscription · $1,788 total
              </p>

              {/* What You Unlock */}
              <div className="space-y-3">
                {proFeatureCategories.map((category, catIdx) => (
                  <div key={catIdx}>
                    <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-wide mb-1.5">
                      {category.title}
                    </p>
                    <ul className="space-y-1">
                      {category.features.map((feature, featIdx) => (
                        <li key={featIdx} className="flex items-start gap-2 text-xs text-white/90">
                          <Check className="w-3 h-3 text-[#FF6E4C] mt-0.5 shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Closing line */}
              <div className="mt-4 pt-3 border-t border-white/15">
                <p className="text-[11px] text-white/70 italic">
                  DVA Pro delivers what's defensible.
                </p>
              </div>
            </div>

            {/* Current Plan - MUTED/SECONDARY */}
            <div className="bg-[#FAFBFC] rounded-lg border border-[#e5e7eb]/70 p-4 opacity-80">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-[#717182]">Current Plan</h3>
                <span className="text-[10px] font-medium px-1.5 py-0.5 bg-[#F0F1F3] text-[#717182] rounded">
                  Freemium
                </span>
              </div>
              <ul className="space-y-1.5">
                {freemiumFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-[#717182]">
                    <Check className="w-3 h-3 text-[#B0B3B8] mt-0.5 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Built for Healthcare - subtle */}
            <div className="bg-white rounded-lg border border-[#e5e7eb] p-3">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-[#18365E]" />
                <p className="text-[10px] font-semibold text-[#18365E] uppercase tracking-wide">
                  Built for Healthcare Enterprises
                </p>
              </div>
              <p className="text-[11px] text-[#717182] leading-relaxed">
                Designed for VP Analytics, CDOs, CFOs, and strategy leaders. 
                Supports real approval workflows, not exploratory dashboards.
              </p>
            </div>
          </div>

          {/* Right Column: Payment Form */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            {/* Mobile: Price Summary + CTA first */}
            <div className="lg:hidden mb-4">
              <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-[#18365E]">DVA Pro</p>
                    <p className="text-xs text-[#717182]">12-month subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#18365E]">$149<span className="text-sm font-normal text-[#717182]">/mo</span></p>
                  </div>
                </div>
                
                {/* Mobile CTA */}
                <button
                  onClick={handleUpgrade}
                  disabled={isProcessing}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-white font-semibold transition-all ${
                    isProcessing
                      ? 'bg-[#18365E]/70 cursor-wait'
                      : 'bg-gradient-to-r from-[#FF6E4C] to-[#FF8F73] hover:from-[#FF5C3A] hover:to-[#FF7D61] shadow-md'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Upgrade Now
                    </>
                  )}
                </button>
                
                {/* Mobile Trust Badges */}
                <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-[#717182]">
                  <div className="flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    SOC 2
                  </div>
                  <span className="text-[#e5e7eb]">·</span>
                  <div className="flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    256-bit
                  </div>
                  <span className="text-[#e5e7eb]">·</span>
                  <span>Healthcare-grade</span>
                </div>
              </div>
            </div>
            
            {/* Payment Form Card - quieter styling */}
            <div className="bg-white rounded-xl border border-[#e5e7eb]/80 shadow-sm overflow-hidden">
              {/* Form Header - with Demo Checkout label */}
              <div className="px-5 py-3 border-b border-[#e5e7eb]/60 bg-[#FAFBFC]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-[#18365E]" />
                    </div>
                    <div>
                      <h2 className="text-sm font-medium text-[#18365E]">Payment Details</h2>
                      <p className="text-[10px] text-[#717182]">Secure checkout</p>
                    </div>
                  </div>
                  <span className="text-[9px] font-medium px-2 py-0.5 bg-blue-50 text-blue-600 rounded uppercase tracking-wide">
                    Demo Checkout
                  </span>
                </div>
              </div>

              {/* Form Body - lighter touch */}
              <div className="p-5 space-y-4">
                {/* Organization (pre-filled) */}
                <div>
                  <label className="block text-[11px] font-medium text-[#717182] mb-1">
                    Organization
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F5F7FA] rounded-lg border border-[#e5e7eb]/60">
                    <Building2 className="w-3.5 h-3.5 text-[#717182]" />
                    <span className="text-sm text-[#18365E]">{DEMO_CLIENT.name}</span>
                  </div>
                </div>

                {/* Card Number */}
                <div>
                  <label className="block text-[11px] font-medium text-[#717182] mb-1">
                    Card Number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      placeholder="4242 4242 4242 4242"
                      className="w-full px-3 py-2.5 border border-[#e5e7eb]/80 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 focus:border-[#18365E]/40 placeholder:text-[#B0B3B8]"
                    />
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <div className="w-7 h-4 bg-[#1A1F71] rounded text-white text-[7px] font-bold flex items-center justify-center">
                        VISA
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expiry and CVC */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-[#717182] mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      value={expiry}
                      onChange={(e) => setExpiry(e.target.value)}
                      placeholder="MM / YY"
                      className="w-full px-3 py-2.5 border border-[#e5e7eb]/80 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 focus:border-[#18365E]/40 placeholder:text-[#B0B3B8]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-[#717182] mb-1">
                      CVC
                    </label>
                    <input
                      type="text"
                      value={cvc}
                      onChange={(e) => setCvc(e.target.value)}
                      placeholder="123"
                      className="w-full px-3 py-2.5 border border-[#e5e7eb]/80 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 focus:border-[#18365E]/40 placeholder:text-[#B0B3B8]"
                    />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div>
                  <label className="block text-[11px] font-medium text-[#717182] mb-1">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-3 py-2.5 border border-[#e5e7eb]/80 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 focus:border-[#18365E]/40 placeholder:text-[#B0B3B8]"
                  />
                </div>

                {/* Subscription Details - compact */}
                <div className="bg-[#F8F9FA] rounded-lg p-3">
                  <h3 className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
                    Subscription Summary
                  </h3>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#717182] text-xs">Plan</span>
                      <span className="text-[#18365E] font-medium text-xs">DVA Pro</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#717182] text-xs">Term</span>
                      <span className="text-[#18365E] font-medium text-xs">12 months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#717182] text-xs">Billing</span>
                      <span className="text-[#18365E] font-medium text-xs">$149/mo</span>
                    </div>
                    <div className="border-t border-[#e5e7eb] pt-1.5 mt-1.5">
                      <div className="flex justify-between">
                        <span className="text-[#18365E] font-medium text-xs">Total contract</span>
                        <span className="text-[#18365E] font-bold text-sm">$1,788</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[9px] text-[#717182]/80 mt-2 leading-relaxed">
                    Early termination does not relieve payment obligations for the full 12-month term.
                  </p>
                </div>

                {/* Demo Notice - subtle */}
                <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-2.5">
                  <p className="text-[11px] text-blue-600">
                    <strong>Demo Mode:</strong> Clicking "Upgrade Now" unlocks Pro features. No payment processed.
                  </p>
                </div>

                {/* Desktop Submit Button */}
                <div className="hidden lg:block">
                  <button
                    onClick={handleUpgrade}
                    disabled={isProcessing}
                    className={`w-full flex flex-col items-center justify-center gap-0.5 px-5 py-3.5 rounded-xl text-white font-semibold transition-all ${
                      isProcessing
                        ? 'bg-[#18365E]/70 cursor-wait'
                        : 'bg-gradient-to-r from-[#FF6E4C] to-[#FF8F73] hover:from-[#FF5C3A] hover:to-[#FF7D61] shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </div>
                    ) : (
                      <>
                        <span className="flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Upgrade Now
                        </span>
                        <span className="text-[11px] font-normal text-white/80">
                          $149/month · 12-month subscription
                        </span>
                      </>
                    )}
                  </button>
                  
                  {/* Trust Badges - directly under CTA */}
                  <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-[#717182]">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      SOC 2 compliant
                    </div>
                    <span className="text-[#e5e7eb]">·</span>
                    <div className="flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      256-bit encryption
                    </div>
                    <span className="text-[#e5e7eb]">·</span>
                    <span>Healthcare-grade data handling</span>
                  </div>
                </div>

                {/* Terms / Legal */}
                <p className="text-[9px] text-center text-[#717182] leading-relaxed pt-1">
                  By upgrading, you agree to a 12-month subscription under our{' '}
                  <a href="#" className="underline hover:text-[#18365E]">Terms of Service</a>{' '}
                  and{' '}
                  <a href="#" className="underline hover:text-[#18365E]">Privacy Policy</a>.
                </p>
              </div>
            </div>

            {/* Back Link */}
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 text-xs text-[#717182] hover:text-[#18365E] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to previous page
              </button>
            </div>
          </div>
        </div>
      </main>
      
      {/* CSS for toast animation */}
      <style>{`
        @keyframes slide-in-from-top-2 {
          from {
            transform: translateY(-8px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-in {
          animation-duration: 300ms;
          animation-fill-mode: both;
        }
        .slide-in-from-top-2 {
          animation-name: slide-in-from-top-2;
        }
        .fade-in {
          animation-name: fade-in;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}